import { startOfWeek, startOfMonth, parseISO, format } from "date-fns";

import type { WasteStats, WasteStatsBucket } from "@/hooks/use-waste-stats";
import type { WeatherDailyBucket } from "@/hooks/use-dashboard-weather";

type Granularity = "day" | "week" | "month";

/** The day/week/month key a date rolls up into — shared by waste + weather series. */
function bucketKey(isoDate: string, granularity: Granularity): string {
  const d = parseISO(isoDate);
  if (granularity === "week") return format(startOfWeek(d, { weekStartsOn: 1 }), "yyyy-MM-dd");
  if (granularity === "month") return format(startOfMonth(d), "yyyy-MM-dd");
  return isoDate;
}

export type FoodTotals = {
  totalWeight: number;
  totalCost: number;
  eventCount: number;
};

/**
 * Headline totals are food-only (POS2-861, Option A): the packaging bucket's
 * weight and count are subtracted from the RPC totals, mirroring cost (forced
 * non-food is costed NULL at source, so its bucket already sums to £0). The
 * packaging bucket itself stays visible in breakdowns and countable for the
 * contamination metric (POS2-856).
 */
export function foodTotals(
  // Narrowed to what the function reads, so the report payload — which ships the waste-type dim and
  // the day dim and nothing else — can pass it without carrying dims no report renders (POS2-1136).
  stats: Pick<WasteStats, "total_events" | "total_weight" | "total_cost" | "by_waste_type">,
): FoodTotals {
  const packaging = stats.by_waste_type.find((b) => b.key === "packaging");
  return {
    totalWeight: stats.total_weight - (packaging?.total_weight ?? 0),
    totalCost: stats.total_cost - (packaging?.total_cost ?? 0),
    eventCount: stats.total_events - (packaging?.event_count ?? 0),
  };
}

export type ReportKpis = {
  /** Food-only headline totals (POS2-861): packaging excluded. */
  totalWeight: number;
  totalCost: number;
  eventCount: number;
  avgWeight: number;
  /** Share of ALL binned events at tier L1 (0–1) — packaging events are L1, so a food-only denominator could exceed 1. */
  l1Rate: number;
  /** Display label of the heaviest waste type, or null when there's no waste. Reads the breakdown, where packaging stays visible. */
  /** The waste-type *key*, not its wording. `deriveKpis` is a pure data function with no locale
   *  and no `t`; the surface that renders it translates through `wasteTypeLabel` (POS2-1134). */
  topWasteTypeKey: string | null;
};

export function deriveKpis(stats: WasteStats): ReportKpis {
  const { totalWeight, totalCost, eventCount } = foodTotals(stats);
  const l1 = stats.by_classification_level.find((b) => b.key === "L1");
  const topType = stats.by_waste_type.reduce<WasteStatsBucket | null>(
    (best, b) => (best === null || b.total_weight > best.total_weight ? b : best),
    null,
  );
  return {
    totalWeight,
    totalCost,
    eventCount,
    avgWeight: eventCount > 0 ? totalWeight / eventCount : 0,
    l1Rate: stats.total_events > 0 ? (l1?.event_count ?? 0) / stats.total_events : 0,
    topWasteTypeKey: topType && topType.total_weight > 0 ? topType.key : null,
  };
}

/** Sums per-day buckets into Monday-started ISO weeks, keyed by the week's start day. */
export function aggregateWeekly(byDay: WasteStatsBucket[]): WasteStatsBucket[] {
  const weeks = new Map<string, WasteStatsBucket>();
  for (const day of byDay) {
    const weekKey = format(startOfWeek(parseISO(day.key), { weekStartsOn: 1 }), "yyyy-MM-dd");
    const existing = weeks.get(weekKey);
    if (existing) {
      existing.event_count += day.event_count;
      existing.total_weight += day.total_weight;
      existing.total_cost += day.total_cost;
    } else {
      weeks.set(weekKey, { ...day, key: weekKey });
    }
  }
  return [...weeks.values()].sort((a, b) => a.key.localeCompare(b.key));
}

/** Sums per-day buckets into calendar months, keyed by the month's first day. */
export function aggregateMonthly(byDay: WasteStatsBucket[]): WasteStatsBucket[] {
  const months = new Map<string, WasteStatsBucket>();
  for (const day of byDay) {
    const monthKey = format(startOfMonth(parseISO(day.key)), "yyyy-MM-dd");
    const existing = months.get(monthKey);
    if (existing) {
      existing.event_count += day.event_count;
      existing.total_weight += day.total_weight;
      existing.total_cost += day.total_cost;
    } else {
      months.set(monthKey, { ...day, key: monthKey });
    }
  }
  return [...months.values()].sort((a, b) => a.key.localeCompare(b.key));
}

/**
 * Means daily sunshine hours into the same day/week/month keys the trend chart
 * uses, so the weather line aligns with the waste series. Days without a reading
 * are skipped; a period with no readings simply has no entry (rendered as a gap).
 */
export function aggregateSunshineByKey(
  weather: WeatherDailyBucket[],
  granularity: Granularity,
): Map<string, number> {
  const groups = new Map<string, { sum: number; count: number }>();
  for (const day of weather) {
    if (day.sunshine_hours == null) continue;
    const key = bucketKey(day.date, granularity);
    const group = groups.get(key) ?? { sum: 0, count: 0 };
    group.sum += day.sunshine_hours;
    group.count += 1;
    groups.set(key, group);
  }
  const means = new Map<string, number>();
  for (const [key, { sum, count }] of groups) means.set(key, sum / count);
  return means;
}

/**
 * Food waste as a share of food spend (cost ÷ spend). Null — rendered "N/A", never
 * Infinity — when spend is missing or non-positive. Food spend is entered via the
 * Food Spend panel (POS2-756); until then callers pass null.
 */
export function foodWastePercent(totalCost: number, foodSpend: number | null): number | null {
  if (foodSpend == null || foodSpend <= 0) return null;
  return totalCost / foodSpend;
}
